import { useQuery, useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import type { PaginatedResponse } from '@aegislab/shared';
import {
  fetchPendingCorrections,
  fetchReviewedCorrections,
  type ReviewedCorrectionsParams,
} from '../api/correctionApi';
import { correctionKeys } from './correctionKeys';

// X3 단계 1b (D6·D8): admin scope correction queue read hooks.
// query key namespacing: ['corrections','admin', <scope>, ...args] — `correctionKeys` 소유.
// 호출자 갱신은 단계 1d.

export function usePendingCorrections(enabled = true) {
  return useQuery({
    queryKey: correctionKeys.adminPending(),
    queryFn: async () => {
      const response = await fetchPendingCorrections();
      return response.data;
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    enabled,
  });
}

/**
 * 처리된 정정 요청 — 커서 페이지를 이어 붙여 `data`(전체 행)·`hasMore` 로 돌려준다.
 * 다음 페이지는 `fetchNextPage` — 첫 페이지만 읽으면 기간 안 100건 너머가 표시 없이 빠진다.
 */
export function useReviewedCorrections(
  params: Omit<ReviewedCorrectionsParams, 'cursor'>,
  enabled = true
) {
  return useInfiniteQuery({
    queryKey: correctionKeys.adminReviewed(params),
    queryFn: ({ pageParam }) => fetchReviewedCorrections({ ...params, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMore ? (last.nextCursor ?? undefined) : undefined),
    select: (cache) => {
      const last = cache.pages[cache.pages.length - 1];
      return {
        data: cache.pages.flatMap((page) => page.data),
        hasMore: last?.hasMore ?? false,
        nextCursor: last?.nextCursor ?? null,
      } satisfies PaginatedResponse<(typeof cache.pages)[number]['data'][number]>;
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    enabled,
  });
}
